import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Info,
  BackgroundBox,
  ShowMoreButton,
  FloatRight,
  PrintButton,
  Icon,
} from './basic';
import ContactInfoRow from './ContactInfoRow';
import translate from '../utils/translate';
import { prepareMarkdown, trimmed } from '../utils/helpers';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

const PrintServiceChannelButton = styled(PrintButton).attrs(
  {
    'aria-label': 'tulosta palvelun tiedot',
  },
)``;

const ContactWrapper = styled.div`
  @media (min-width: 799px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-gap: 32px;
  }
`;

const Box = styled(BackgroundBox)`
  margin-left: 0;
  margin-right: 0;
`;

const Title = styled.h4`
  margin-top: 0;
  display: inline-flex;
`;

const SubTitle = styled.h5`
  @media (min-width: 799px) {
    margin-top: 0;
  }
`;

const Description = styled.div`
  margin-bottom: 24px;
`;

const InfoArea = styled.div`
  margin: 16px 0;
  font-size: 0.9em;
  font-weight: 600;
`;

const Hours = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
`;

const OpeningInfo = styled.div`
  margin: 12px 0;
  font-weight: normal;
`;

function ServiceChannel({ data, index }) {
  const [
    showFullDescription,
    setShowFullDescription,
  ] = useState(false);

  const renderDescription = () =>
    data.description.length > 200 &&
    !showFullDescription ? (
      <Description>
        <p>
          {trimmed(data.description)}
          <ShowMoreButton
            className="dont-print"
            onClick={() => setShowFullDescription(true)}
          >
            Näytä kokonaan
          </ShowMoreButton>
        </p>
      </Description>
    ) : (
      <Description
        dangerouslySetInnerHTML={{
          __html: prepareMarkdown(data.description),
        }}
      />
    );

  const renderPhoneNumbers = () => (
    <Info>
      {data.phoneNumbers.map((item) => (
        <ContactInfoRow
          key={`${index}:${item.number}`}
          value={item.number}
          info={item.info}
          type="phone"
        />
      ))}
    </Info>
  );

  const renderWebPages = () => (
    <Info>
      {data.webPages.map((item) => (
        <ContactInfoRow
          key={`${index}:${item.url}`}
          value={item.url}
          info={item.info}
          type="link"
        />
      ))}
    </Info>
  );

  const renderEmails = () => {
    return (
      <Info>
        {data.emails.map((item) => (
          <ContactInfoRow
            key={`${index}:${item.email}`}
            value={item.email}
            info={item.info}
            type="email"
          />
        ))}
      </Info>
    );
  };

  const renderAddresses = () => (
    <Info>
      {data.addresses.map((item) => (
        <ContactInfoRow
          key={`${index}:${item.address}:${item.type}`}
          value={item.address}
          info={translate(item.type)}
          type="address"
        />
      ))}
    </Info>
  );

  const renderServiceHours = () =>
    data.serviceHours.map((item) => (
      <div key={item.key}>
        {(item.info || item.validFromTo) && (
          <OpeningInfo>
            {!!item.info && <div>{item.info} </div>}
            {!!item.validFromTo && (
              <div>{item.validFromTo}</div>
            )}
          </OpeningInfo>
        )}

        {item.isClosed && <div>SULJETTU</div>}
        {item.isAlwaysOpen && <div>AINA AUKI</div>}
        {item.isReservation && <div>VARAUKSEN MUKAAN</div>}
        {!!item.openingHour.length && (
          <>
            <Hours>
              {item.openingHour.map(({ day, time }) => (
                <React.Fragment
                  key={`${item.key}:${day}:${time}`}
                >
                  <div>{day}</div>
                  <div>{time}</div>
                </React.Fragment>
              ))}
            </Hours>
          </>
        )}
      </div>
    ));

  function printServiceChannel(evt) {
    const currentTarget = evt.currentTarget.value;
    const element = document.getElementById(
      'print-service-channel-' + currentTarget,
    );

    const serviceDetailButton = element.querySelector(
      'div p button',
    );
    if (serviceDetailButton) {
      serviceDetailButton.click();
    }

    element.classList.remove('service-channel-box');
    window.print();
    element.classList.add('service-channel-box');
  }

  return !data ? null : (
    <Box
      className="service-channel-box"
      id={'print-service-channel-' + data.id}
    >
      <Title>{data.name}</Title>

      <FloatRight>
        <PrintServiceChannelButton
          onClick={printServiceChannel}
          value={data.id}
        >
          <Icon icon={faPrint} />
          Tulosta
        </PrintServiceChannelButton>
      </FloatRight>

      {!!data.description && renderDescription()}

      <ContactWrapper>
        <div>
          <SubTitle>YHTEYSTIEDOT</SubTitle>
          <InfoArea>
            {!!data.phoneNumbers?.length &&
              renderPhoneNumbers()}
            {!!data.webPages?.length && renderWebPages()}
            {!!data.emails?.length && renderEmails()}
            {!!data.addresses?.length && renderAddresses()}
          </InfoArea>
        </div>
        {!!data.serviceHours?.length && (
          <div>
            <SubTitle>AUKIOLOTIEDOT</SubTitle>
            <InfoArea>{renderServiceHours()}</InfoArea>
          </div>
        )}
      </ContactWrapper>
    </Box>
  );
}

export default ServiceChannel;
